<template>
  <div class="terms-of-service">
    <h1>Terms of Service</h1>
    <p>Last updated: November 2, 2026</p>

    <p>
      These Terms of Service (hereinafter "Terms") govern your access to and use of Zearloops websites, mobile applications, games, and
      related services (collectively, the "Service"). By accessing or using the Service, you agree to be bound by these Terms. Our Privacy
      Policy, which explains how we collect and process personal data, is available at
      <a href="https://zearloops.com/privacy-policy" target="_blank" rel="noopener">zearloops.com/privacy-policy</a> and is incorporated
      by reference into these Terms. If you do not agree to these Terms, you must not access or use the Service.
    </p>

    <h2>1. Terms and Definitions</h2>
    <p>As used in these Terms:</p>
    <ul>
      <li><strong>"Zearloops," "we," "us," or "our"</strong> refers to Zearloops and all affiliated entities.</li>
      <li><strong>"You" or "your"</strong> refers to any user of the Service.</li>
      <li><strong>"Service"</strong> refers to all Zearloops mobile applications (including Epic Rifts and Cute &amp; Cursed Wallpapers), websites, web applications, and related features and platforms.</li>
      <li><strong>"In-Game Account"</strong> refers to the account created by a user on one of the Zearloops applications (including guest accounts and authenticated accounts via Google Play Games or Apple Game Center).</li>
      <li><strong>"Zearloops Content"</strong> refers to all text, graphics, images, game assets, characters, levels, audio, trademarks, logos, software, and other content made available by Zearloops through the Service.</li>
      <li><strong>"Virtual Items"</strong> refers to Zearloops Content that can be obtained or purchased within the Service for entertainment use, including but not limited to weapons, equipment, cosmetics, bundles, and other in-game objects.</li>
      <li><strong>"Virtual Currency"</strong> refers to in-game currencies (including Gold, Gems, Boss Souls, Monster Souls, PvP Coins, and Abyss Shards) that can be acquired through gameplay or purchased and are used within the Service.</li>
      <li><strong>"User Content"</strong> refers to a display name you choose for your in-game profile and the text, optional contact details, and screenshots or images that you choose to include in a bug report submitted through the Application.</li>
      <li><strong>"Intellectual Property Rights"</strong> refers to all patents, copyrights, trademarks, service marks, trade names, database rights, domain name rights, moral rights, trade secrets, and any other intellectual property rights owned by or licensed to Zearloops.</li>
    </ul>

    <h2>2. Consent to These Terms</h2>
    <p>
      When you first open a Zearloops application, a dialog asks you to accept these Terms of Service and acknowledge our Privacy Policy.
      By tapping "I Accept," you agree to these Terms and acknowledge the Privacy Policy. This acceptance is not blanket consent to all
      processing; where applicable law requires consent for a specific purpose, that consent must be obtained separately.
    </p>
    <p>
      The consent dialog also requires you to confirm that you are 16 years of age or older, or that a parent or guardian holding parental
      responsibility consents on your behalf.
    </p>

    <h2>3. Use of the Service</h2>
    <p>
      Subject to your compliance with these Terms, Zearloops grants you a limited, non-exclusive, non-transferable, non-sublicensable
      license to access and use the Service solely for your personal, non-commercial entertainment purposes.
    </p>
    <p>You may not use the Service for any purpose that is unlawful or prohibited by these Terms. In particular, you agree not to:</p>
    <ul>
      <li>Violate any applicable law or regulation, or infringe any third-party rights.</li>
      <li>Use cheats, bots, exploits, automation tools, or unauthorized third-party software that interact with the Service.</li>
      <li>Harass, threaten, stalk, intimidate, or abuse other users, or incite others to commit violence.</li>
      <li>Upload, transmit, or post any User Content that is unlawful, harmful, defamatory, obscene, vulgar, hateful, invasive of another's privacy, or that infringes any intellectual property right.</li>
      <li>Introduce viruses, worms, Trojan horses, or other malicious code into the Service.</li>
      <li>Attempt to gain unauthorized access to any computer system, server, or database used by Zearloops.</li>
      <li>Attempt to reverse engineer, decompile, or disassemble any part of the Service except to the extent expressly permitted by applicable law.</li>
      <li>Copy, modify, host, stream, sublicense, or resell any portion of the Service or Zearloops Content, except as expressly permitted in these Terms.</li>
      <li>Enable or allow others to access the Service using your In-Game Account.</li>
      <li>Misrepresent your identity, impersonate another person, or use a false name.</li>
      <li>Collect or harvest information about other users of the Service for any purpose not expressly permitted by these Terms, including sending unsolicited commercial messages.</li>
      <li>Use information about users publicly available in the Service (e.g., leaderboards) for any purpose unrelated to the Service.</li>
      <li>Make false reports to Zearloops staff or misuse in-game support systems.</li>
      <li>Place any advertisement of products or services within the Service without Zearloops's prior written approval.</li>
      <li>Conduct fraudulent activities or circumvent any usage restrictions put in place by these Terms.</li>
      <li>Submit false, misleading, or abusive bug reports; misuse the in-game reporting system for any purpose other than reporting genuine technical issues; or attach to a bug report any screenshot or image that is unlawful, contains another person's personal information without their consent, or is unrelated to the issue being reported.</li>
    </ul>
    <p>
      You are welcome to provide feedback on the Service at any time by emailing us at
      <a href="mailto:support@zearloops.com">support@zearloops.com</a>.
    </p>

    <h2>4. Accounts, Eligibility, and Registration</h2>
    <p>
      You must be at least 16 years of age to use the Service freely. If you are under 16, you may only use the Service with the consent
      of your parent or guardian as described in Section 2. By registering an In-Game Account or otherwise using the Service, you confirm
      that you meet this age requirement or that you have obtained the required parental consent.
    </p>
    <p>
      You are responsible for maintaining the confidentiality of your account credentials and for all activity that occurs under your
      In-Game Account, whether or not you authorized it. You agree not to share your account credentials with any third party. You must
      notify us immediately of any unauthorized use of your account at
      <a href="mailto:support@zearloops.com">support@zearloops.com</a>.
    </p>
    <p>
      You have no property right or ownership interest in your In-Game Account. All In-Game Accounts are the property of Zearloops.
      You may not sell, transfer, or otherwise assign your In-Game Account or any rights therein to any third party.
    </p>
    <p>
      Zearloops reserves the right to refuse registration of, or cancel, any username at its sole discretion. You agree to provide
      accurate, current, and complete information during registration and to keep such information up to date. Failure to do so may
      result in termination of your account.
    </p>

    <h2>5. Virtual Items, Virtual Currency, and Purchases</h2>
    <p>
      Virtual Items and Virtual Currency are provided to you solely as a limited, non-exclusive, non-transferable, revocable license for
      personal entertainment use within the Service. They have no "real world" monetary value, cannot be redeemed for cash or any other
      consideration, and are not your personal property. No ownership interest in Virtual Items or Virtual Currency is transferred to you.
    </p>
    <p>
      Availability, pricing, and balancing of Virtual Items and Virtual Currency may be changed at any time, with or without notice, to
      maintain game health. Zearloops may manage, modify, or eliminate Virtual Items and Virtual Currency at any time without liability
      to you or any third party.
    </p>
    <p>
      You may not sell, transfer, or attempt to sell or transfer Virtual Items or Virtual Currency to any other person for real-world
      money or any item of real monetary value. Any such attempted transfer is void and may result in termination of your account.
    </p>
    <p>
      All in-app purchases are final and non-refundable except as required by applicable law or the applicable platform's (Google Play,
      Apple App Store) refund policies. We do not store your payment card details; all transactions are processed by authorized
      third-party payment processors. All in-app transactions are also subject to those processors' terms of use.
    </p>
    <p>
      Some VIP products are recurring subscriptions billed weekly or monthly, as shown in the purchase confirmation screen. Unless you
      cancel through Google Play or the Apple App Store before the current billing period ends, the subscription renews automatically
      at the price and for the period shown by that platform. An introductory three-day free trial may be offered with an eligible weekly plan;
      unless canceled before the trial ends, the subscription converts to a paid plan at the displayed price. You can manage or cancel
      a subscription in the store account used to purchase it. Deleting your game account does not cancel a store subscription.
      Lifetime VIP is a one-time purchase, not a recurring subscription. Store billing, cancellation, and refund rules also apply.
    </p>
    <p>
      <strong>Important:</strong> If your In-Game Account is deleted — whether by you, by Zearloops for violation of these Terms, or for
      any other reason — you will permanently and irrevocably lose all Virtual Items and Virtual Currency associated with that account,
      including any that were purchased with real money. Account deletion does not itself entitle you to a refund or compensation; any
      refund rights under applicable law and platform policies remain unaffected.
    </p>
    <p>
      You represent and warrant that you are not located in a country subject to a U.S. Government embargo or designated as a
      "terrorist-supporting" country, and that you are not listed on any U.S. Government list of prohibited or restricted parties.
    </p>

    <h2>6. User Content</h2>
    <p>
      You retain any rights you have in your User Content. You grant Zearloops a limited, non-exclusive, worldwide, royalty-free
      license to store and display your chosen in-game display name as needed to operate the Service, including profiles, rankings,
      and PvP features. When you submit a bug report, you also grant Zearloops a limited license to receive, host, reproduce, and
      review the report and any attached images only to investigate and respond to it, maintain service security, and comply with law.
      Service providers acting on our behalf may process this content for those purposes. Report content is subject to the retention
      period described in our Privacy Policy.
    </p>
    <p>
      Zearloops does not claim ownership of your User Content. You remain responsible for all User Content you submit and represent
      that: (i) you own or have the necessary rights to grant the license above; and (ii) your User Content does not infringe any
      third-party intellectual property, privacy, or other rights, and does not violate any applicable law.
    </p>

    <h2>7. Intellectual Property</h2>
    <p>
      All Zearloops Content is protected by copyright, trademark, and other intellectual property laws. Except as expressly set out
      in these Terms, Zearloops and its licensors exclusively own all Intellectual Property Rights in and to the Service and all
      Zearloops Content. You will not remove, alter, or obscure any copyright, trademark, service mark, or other proprietary notice
      incorporated in or accompanying the Service or Zearloops Content.
    </p>
    <p>
      You may not reproduce, distribute, create derivative works from, publicly display, or commercially exploit any Zearloops Content
      without prior written permission from Zearloops.
    </p>

    <h2>8. Copyright Infringement and DMCA</h2>
    <p>
      If you believe that any User Content available through the Service infringes your copyright, you may submit a Digital Millennium
      Copyright Act (DMCA) notice to us at <a href="mailto:contact@zearloops.com">contact@zearloops.com</a>. A valid DMCA notice must include:
    </p>
    <ul>
      <li>A description of the copyrighted work you claim has been infringed.</li>
      <li>A description of where the allegedly infringing material is located within the Service.</li>
      <li>A statement that you have a good-faith belief that the use is not authorized by the copyright owner, its agent, or the law.</li>
      <li>Your contact information (name, address, telephone number, and email address).</li>
      <li>A statement, made under penalty of perjury, that the information in your notice is accurate and that you are the copyright owner or authorized to act on the copyright owner's behalf.</li>
      <li>Your physical or electronic signature.</li>
    </ul>
    <p>
      If you believe content was disabled as a result of an erroneous DMCA notice, you may submit a DMCA Counter Notice to
      <a href="mailto:contact@zearloops.com">contact@zearloops.com</a> containing: (i) a description of the removed content and its
      former location; (ii) a statement under penalty of perjury that you have a good-faith belief the content was removed in error;
      (iii) your contact information; and (iv) your physical or electronic signature. We will process valid counter notices in
      accordance with applicable law.
    </p>
    <p>
      Before submitting a DMCA notice, please consider whether the use may be protected by the Fair Use doctrine. Filing a notice
      without a good-faith basis may expose you to liability for costs and attorneys' fees.
    </p>

    <h2>9. Service Availability and Gameplay Changes</h2>
    <p>
      We may update, suspend, or discontinue parts of the Service for maintenance, balancing, security, or legal reasons. Game features,
      characters, progression systems, rewards, Battle Passes, seasonal content, and virtual economy values may change over time.
      These changes do not waive any cancellation, refund, or other rights available under applicable law or platform rules, including
      rights relating to subscriptions or paid content. We are not liable for unavailability caused by maintenance, equipment failure,
      or circumstances beyond our reasonable control, to the extent permitted by law.
    </p>

    <h2>10. Termination and Enforcement</h2>
    <p>
      Zearloops reserves the right to suspend or terminate your In-Game Account, restrict your access to the Service, remove your
      User Content, or take other enforcement actions if we determine that you have violated these Terms or engaged in abusive,
      fraudulent, or illegal behavior. We may act with or without prior notice and have no obligation to restore your account or
      access.
    </p>
    <p>
      If you wish to request restoration of access following a suspension, you may send a request to
      <a href="mailto:support@zearloops.com">support@zearloops.com</a>, including your username, display name, in-game identifier, and
      any other information reasonably needed to identify your account. Zearloops has sole discretion whether to restore access.
    </p>
    <p>
      You may delete your In-Game Account using the Delete Account option in the Application. If you cannot access the Application,
      email <a href="mailto:support@zearloops.com">support@zearloops.com</a> with your in-game user ID so we can locate the account.
      Upon deletion, you will permanently lose all Virtual Items,
      Virtual Currency, game progress, and purchased content licenses associated with your account. Account deletion does not itself
      entitle you to a refund; refund rights under applicable law and platform policies remain unaffected.
      Zearloops will also process erasure of associated personal data in accordance with our Privacy Policy, subject to legal
      retention obligations.
    </p>
    <p>
      Guest In-Game Accounts that are not linked to Google Play Games or Apple Game Center and have been inactive for 180 consecutive days may be automatically deleted if they have no game progress, no purchases, no chosen display name, and minimal play time, as described in our Privacy Policy. Accounts linked to a platform account, or with progress or purchases, are not subject to this automatic deletion. If a guest account is deleted, the Virtual Items and Virtual Currency associated with it are lost, as described in Section 5.
    </p>
    <p>
      If other users report violations of these Terms, Zearloops may investigate and take appropriate action, including account
      suspension or termination. These enforcement measures do not preclude Zearloops from pursuing other legal remedies.
    </p>

    <h2>11. Disclaimers</h2>
    <p>
      To the maximum extent permitted by applicable law, the Service and all Zearloops Content are provided "as is" and "as available"
      without warranty of any kind, express or implied. Zearloops expressly disclaims all warranties of merchantability, fitness for a
      particular purpose, quiet enjoyment, non-infringement, and any warranties arising out of course of dealing or usage of trade.
      Zearloops makes no warranty that the Service will be uninterrupted, secure, error-free, or meet your requirements. No advice or
      information obtained from Zearloops or through the Service will create any warranty not expressly stated in these Terms.
    </p>

    <h2>12. Limitation of Liability</h2>
    <p>
      To the maximum extent permitted by applicable law, Zearloops is not liable for any indirect, incidental, special, consequential,
      or punitive damages, or loss of profits, data, use, goodwill, or other intangible losses arising out of or related to your use
      of or inability to use the Service, regardless of the theory of liability and even if Zearloops has been advised of the
      possibility of such damages.
    </p>
    <p>
      In no event will Zearloops's total aggregate liability arising out of or in connection with these Terms or the Service exceed
      one hundred U.S. dollars (USD $100). These limitations apply to the maximum extent permitted by applicable law and do not
      apply where prohibited by law.
    </p>

    <h2>13. Indemnification</h2>
    <p>
      You agree to indemnify, defend, and hold harmless Zearloops and its officers, directors, employees, agents, and affiliates
      from and against any claims, liabilities, damages, losses, or expenses (including reasonable attorneys' fees) arising out of
      or in any way connected with: (i) your breach of these Terms; (ii) your violation of any law or the rights of a third party;
      (iii) your User Content; or (iv) your use of the Service.
    </p>

    <h2>14. Third-Party Services</h2>
    <p>
      The Service integrates with and may link to third-party services, including but not limited to Firebase (Google LLC), Google
      AdMob, Google Play Games, Apple Game Center, payment processors (Google Play Billing, Apple In-App Purchase), and issue tracking tools (Linear Orbit, Inc.). Those services
      are governed by their own terms and privacy policies. We are not responsible for the content, practices, or availability of
      third-party services; please review their terms separately.
    </p>
    <p>
      The Service may also contain links to external websites or resources. Zearloops assumes no responsibility for such external
      content; your use of those resources is at your own risk.
    </p>

    <h2>15. Governing Law and Dispute Resolution</h2>
    <p>
      These Terms are governed by and construed in accordance with the laws of the State of Wyoming, United States of America,
      without regard to its conflict of laws principles. Where applicable law of other jurisdictions or supranational organizations
      (including but not limited to GDPR, CCPA, or DMCA) mandates certain protections that cannot be excluded, such provisions
      will apply.
    </p>
    <p>
      If you have a concern about these Terms or the Service, please first send a written claim to
      <a href="mailto:contact@zearloops.com">contact@zearloops.com</a>. We will use reasonable efforts to respond within 30 calendar
      days. If the matter is not resolved, you agree that the state and federal courts located in the State of Wyoming shall have
      exclusive jurisdiction to resolve any dispute arising out of or relating to these Terms or the Service, and both parties
      consent to personal jurisdiction in such courts. Nothing in this section limits any non-waivable consumer rights or a right to
      bring a claim in another forum where applicable law requires or permits it.
    </p>

    <h2>16. Language</h2>
    <p>
      The controlling language of these Terms is English. Any translation provided is for convenience only, and the English version
      shall govern to the extent of any inconsistency.
    </p>

    <h2>17. Changes to These Terms</h2>
    <p>
      We may update these Terms periodically. We will post the updated Terms on this page with a revised "Last updated" date.
      Continued use of the Service after changes become effective constitutes your acceptance of the updated Terms. If the modified
      Terms are not acceptable to you, your only recourse is to cease using the Service and request deletion of your account as
      described in Section 10.
    </p>

    <h2>18. Contact</h2>
    <p>
      Questions or concerns about these Terms can be sent to
      <a href="mailto:contact@zearloops.com">contact@zearloops.com</a>.
    </p>
    <p>
      We also reference the following third-party trademarks in connection with the Service:<br/>
      — Apple, the Apple logo, iPhone, and iPad are trademarks of Apple Inc., registered in the U.S. and other countries. App Store is a service mark of Apple Inc.<br/>
      — Google Play and the Google Play logo are trademarks of Google LLC.
    </p>
  </div>
</template>

<script>
export default {
  name: 'TermsOfService',
};
</script>
  